import type { JSX } from 'react';
import { useState } from 'react';
import { Field } from '@/app/ui/Field';
import { Button, Checkbox, controlStyles as c, Segment } from '@/app/ui/controls';
import { MAX_RASTER_SIDE } from '@/core/export/export';
import type { OutputFormat, Project } from '@/core/project/types';
import { pxFromMillimeters } from '@/core/project/types';
import { SIZE_PRESETS } from '@/core/project/defaults';
import { OUTPUT_WIDTH_RANGE } from '@/storage/projectFile';
import type { EditorAction } from '../state';
import styles from './panels.module.css';

export type ExportPanelProps = {
  project: Project;
  /** Реальная высота документа из макета — для оформленной карточки она больше ширины. */
  documentHeight: number | null;
  dispatch: (action: EditorAction) => void;
};

const FORMATS: { value: OutputFormat; label: string; title: string }[] = [
  { value: 'png', label: 'PNG', title: 'Основной растровый формат' },
  { value: 'jpeg', label: 'JPEG', title: 'Только для систем, которые не принимают PNG' },
  { value: 'svg', label: 'SVG', title: 'Вектор с подписью в контурах' },
];

export function ExportPanel({ project, documentHeight, dispatch }: ExportPanelProps): JSX.Element {
  const [mm, setMm] = useState(50);
  const [ppi, setPpi] = useState(300);
  const printPx = pxFromMillimeters({ millimeters: mm, ppi });
  const printTooBig = printPx > OUTPUT_WIDTH_RANGE.max;

  return (
    <div className={styles.panel}>
      <Field label="Формат файла">
        {() => (
          <Segment
            name="format"
            ariaLabel="Формат файла"
            value={project.output.format}
            options={FORMATS}
            onChange={(format) => dispatch({ type: 'set-format', format })}
          />
        )}
      </Field>

      {project.output.format === 'jpeg' ? (
        <Field
          label={`Качество JPEG: ${project.output.jpegQuality.toFixed(2)}`}
          hint="JPEG всегда сохраняется с непрозрачным фоном. После сжатия файл проверяется декодером."
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              className={c.slider}
              type="range"
              min={0.5}
              max={1}
              step={0.01}
              value={project.output.jpegQuality}
              onChange={(e) =>
                dispatch({ type: 'set-jpeg-quality', quality: Number(e.target.value) })
              }
            />
          )}
        </Field>
      ) : null}

      <Field
        label="Ширина документа"
        hint={
          documentHeight !== null && documentHeight !== project.output.widthPx
            ? `Вся композиция: ${project.output.widthPx} × ${documentHeight} px.`
            : `Предельная сторона растрового файла — ${MAX_RASTER_SIDE} px.`
        }
      >
        {() => (
          <Segment
            name="size"
            ariaLabel="Ширина документа"
            value={String(project.output.widthPx)}
            options={SIZE_PRESETS.map((size) => ({ value: String(size), label: `${size} px` }))}
            onChange={(value) => dispatch({ type: 'set-width', widthPx: Number(value) })}
          />
        )}
      </Field>

      <Field
        label="Произвольная ширина, px"
        hint="Размер модуля пересчитывается целыми пикселями, остаток уходит во внешний отступ."
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            className={c.input}
            type="number"
            min={OUTPUT_WIDTH_RANGE.min}
            max={OUTPUT_WIDTH_RANGE.max}
            step={16}
            value={project.output.widthPx}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (!Number.isFinite(raw)) return;
              const clamped = Math.min(
                OUTPUT_WIDTH_RANGE.max,
                Math.max(OUTPUT_WIDTH_RANGE.min, Math.round(raw)),
              );
              dispatch({ type: 'set-width', widthPx: clamped });
            }}
          />
        )}
      </Field>

      <fieldset className={styles.panel} style={{ border: 0, margin: 0, padding: 0 }}>
        <p className={styles.sizeNote}>
          Расчёт для печати: px = мм ÷ 25,4 × ppi. Это только размер в пикселях. Стандартный
          браузерный canvas записывает в метаданные 96 dpi, поэтому для точных физических размеров
          лучше использовать SVG.
        </p>
        <div className={styles.two}>
          <Field label="Физический размер, мм">
            {({ id }) => (
              <input
                id={id}
                className={c.input}
                type="number"
                min={5}
                max={500}
                value={mm}
                onChange={(e) => setMm(Number(e.target.value) || 0)}
              />
            )}
          </Field>
          <Field label="Плотность, ppi">
            {({ id }) => (
              <input
                id={id}
                className={c.input}
                type="number"
                min={72}
                max={1200}
                step={1}
                value={ppi}
                onChange={(e) => setPpi(Number(e.target.value) || 0)}
              />
            )}
          </Field>
        </div>
        <div className={c.row}>
          <span className={styles.sizeNote}>
            {mm} мм при {ppi} ppi — это {printPx} px.
            {printTooBig ? ` Больше предела ${OUTPUT_WIDTH_RANGE.max} px для растра.` : ''}
          </span>
          <Button
            disabled={printPx < OUTPUT_WIDTH_RANGE.min || printTooBig}
            onClick={() => dispatch({ type: 'set-width', widthPx: printPx })}
          >
            Подставить в ширину
          </Button>
        </div>
      </fieldset>

      <Checkbox
        checked={project.output.transparentBackground}
        disabled={project.output.format === 'jpeg'}
        onChange={(value) => dispatch({ type: 'set-transparent', value })}
        label="Прозрачный фон вокруг карточки"
        hint={
          project.output.format === 'jpeg'
            ? 'JPEG не поддерживает прозрачность — для него всегда подставляется белый фон.'
            : 'Поле под кодом и защитная зона остаются непрозрачными. Фон места размещения нам неизвестен, проверьте результат на реальном фоне.'
        }
      />
    </div>
  );
}
